import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useState, type ReactNode } from 'react'
import { useLocation } from 'react-router'
import { useShownLocation } from '@/lib/photoMotion'
import { ApiError, type Me } from '@/lib/api'
import { useFamily } from '@/lib/families'
import { opensOnCircle, useFrame } from '@/lib/frame'
import { mySections } from '@/lib/sections'
import { useIsPhone, useIsWide } from '@/lib/platform'
import { useOnline } from '@/lib/offline'
import { settled, useNewVersion } from '@/lib/update'
import { drainUploads } from '@/lib/uploadQueue'
import { cn } from '@/lib/utils'
import { column, page } from './layout'
import { everyonePlaces, places as familyPlaces } from './nav/places'
import { PhoneHeader, TABS, TabBar } from './nav/PhoneShell'
import { WhereYouAre } from './nav/WhereYouAre'
import { PullToRefresh } from './PullToRefresh'
import { Sidebar, TopBar } from './nav/Sidebar'
import { EveryoneOpens } from './EveryoneOpens'
import { Button } from './ui/button'

const SIDEBAR = 'gatheredfrom:sidebar-open'

// The frame around every signed-in page. On a phone, a slim bar on top with the places as tabs at the bottom, as
// notesfrom has. On a tablet, whichever way it's held, and on a computer, the places are tabs at the top with a
// sidebar that opens from them, the way Apple's iPad apps work.
export function Shell({ me, children }: { me: Me; children: ReactNode }) {
  const online = useOnline()
  // Under an open photo, the frame stays the page's own (lib/photoMotion.ts).
  const address = useLocation()
  const { pathname } = useShownLocation()
  const over = address.pathname !== pathname
  const queryClient = useQueryClient()
  const { familyId, setFamilyId } = useFrame()
  const family = useFamily(familyId ?? '', { enabled: !!familyId })
  const phone = useIsPhone()
  const wide = useIsWide()
  // Beside the page, the sidebar stays open or closed as you left it; over the page, it opens only when asked.
  const [pinned, setPinned] = useState(() => {
    try {
      return localStorage.getItem(SIDEBAR) === '1'
    } catch {
      return false
    }
  })
  // Over the page it belongs to the page it was opened on, so going somewhere closes it.
  const [drawerAt, setDrawerAt] = useState<string | null>(null)
  const drawer = drawerAt === pathname

  // A remembered family you've since left (or that was never yours) is forgotten rather than shown. Losing signal
  // isn't leaving, so only the server saying "not found" counts.
  const gone = family.error instanceof ApiError && family.error.status === 404
  useEffect(() => {
    if (gone) setFamilyId(null)
  }, [gone, setFamilyId])

  // Photos waiting on this phone upload now, and again whenever signal comes back.
  useEffect(() => {
    const drain = () =>
      void drainUploads((recipeId) => {
        void queryClient.invalidateQueries({ queryKey: ['recipe', recipeId] })
        void queryClient.invalidateQueries({ queryKey: ['book'] })
      })
    drain()
    window.addEventListener('online', drain)
    return () => window.removeEventListener('online', drain)
  }, [queryClient])

  // A newer deploy while this stays open: a quiet offer to refresh, which first waits for anything still saving or
  // uploading, so nothing is lost. Kept out of the way while writing or cooking.
  const newVersion = useNewVersion()
  const [refreshing, setRefreshing] = useState(false)
  async function refreshToNewVersion() {
    setRefreshing(true)
    await settled(() => queryClient.isMutating() > 0)
    location.reload()
  }

  function toggleSidebar() {
    if (!wide) return setDrawerAt(drawer ? null : pathname)
    setPinned(!pinned)
    try {
      localStorage.setItem(SIDEBAR, pinned ? '0' : '1')
    } catch {
      // Not remembered; it still opens and closes.
    }
  }

  // Whether you're in Everyone or a circle: set by the last of the two you were on, so a photo, moment or recipe opened
  // from Everyone keeps Everyone's tabs. Before either, it's where the app opens.
  const inEveryone = pathname === '/everyone' || pathname.startsWith('/everyone/')
  const [fromEveryone, setFromEveryone] = useState(() => !opensOnCircle())
  if (inEveryone && !fromEveryone) setFromEveryone(true)
  if (pathname.startsWith('/circle/') && fromEveryone) setFromEveryone(false)
  const everyone = !!me.everyone && (inEveryone || fromEveryone)

  const current = family.data ?? null
  // A circle without a family tree still has People, as who's here; only the owner's switch takes it away. In your own
  // space it's where you connect with people one to one.
  const shows = current ? mySections(me, current) : null
  const places = (
    everyone
      ? everyonePlaces(pathname, !inEveryone, me.sections)
      : current
        ? familyPlaces(current.id, pathname, true, { ...shows!, people: me.sections.people })
        : []
  ).map((place) => (place.key === 'people' && me.requests ? { ...place, dot: true } : place))
  // A family's home, its photos, moments, people and calendar, a book, a recipe, an album, a photo and someone's page are galleries that lay themselves
  // out across the screen; everything else is a reading or form page in one column at notesfrom's measure.
  // Everyone's pages (/everyone/…) are laid out as a circle's are.
  const circlePathname = pathname.replace(/^\/everyone(?=\/|$)/, '/circle/everyone')
  const gallery =
    /^\/(circle|book|recipe|album|cookbook|photo|profile)\/(?!new$)[^/]+$/.test(circlePathname) ||
    /^\/circle\/[^/]+\/(photos|moments|calendar|people(\/[^/]+)?)$/.test(circlePathname)
  // Writing, cooking or reading a storybook takes the whole phone screen, as writing does in notesfrom.
  const focused = /\/(new|edit|cook|story)$/.test(pathname)
  // Walking the family tree from person to person stays on one page, so the tree can animate the walk itself.
  const pageKey = circlePathname.replace(/^(\/circle\/[^/]+\/people)\/[^/]+$/, '$1')
  // A section's own page (Home, People, Photos, Moments, Recipes) can be pulled down to see what's new.
  const sectionPage = places.some((place) => place.to === pathname)
  const sidebar = places.length > 0 && (wide ? pinned : drawer)
  const beside = sidebar && wide

  return (
    <div className="shell flex min-h-svh" inert={over} aria-hidden={over || undefined}>
      {!phone && places.length > 0 && (
        <Sidebar
          me={me}
          family={everyone ? null : current}
          places={places}
          open={sidebar}
          beside={wide}
          onToggle={toggleSidebar}
        />
      )}
      <div className={cn('flex min-w-0 flex-1 flex-col', beside && 'pl-74')}>
        {phone ? (
          <PhoneHeader
            me={me}
            profile={places.length === 0}
            logo={places.length === 0 || places.some((place) => place.key === 'home' && place.active)}
          />
        ) : (
          <TopBar me={me} places={places} sidebar={sidebar} beside={beside} onToggle={toggleSidebar} />
        )}
        {!online && (
          <p
            role="status"
            className="mx-5 mt-4 rounded-2xl bg-muted px-4 py-3 text-sm text-muted-foreground sm:mx-8 lg:mx-12"
          >
            You’re offline. Recipes you’ve opened are still here.
          </p>
        )}
        <main
          key={pageKey}
          className={cn(
            'flex flex-1 animate-page-in flex-col pb-[calc(var(--tabs-clear)+6rem)] nav:pb-20',
            !gallery && cn(column, 'gap-6 pt-6 nav:pt-14'),
          )}
        >
          {phone && sectionPage && !focused && (
            <div className={cn(gallery && page, 'flex pt-2', !gallery && '-mt-4')}>
              <WhereYouAre
                me={me}
                family={everyone ? null : current}
                places={places.length > TABS ? places.slice(TABS - 1) : []}
              />
            </div>
          )}
          {children}
        </main>
      </div>
      {phone && sectionPage && !over && <PullToRefresh key={pathname} />}
      {!focused && <EveryoneOpens me={me} />}
      {phone && places.length > 0 && <TabBar me={me} places={places} hidden={focused} />}
      {newVersion && !focused && (
        <div
          role="status"
          className="fixed top-[calc(var(--vv-top)+var(--bar-h)+var(--safe-top)+0.5rem)] left-1/2 z-40 flex w-max max-w-[calc(100%-1.5rem)] -translate-x-1/2 animate-page-in items-center gap-3 rounded-full bg-primary py-1.5 pr-1.5 pl-5 text-sm text-primary-foreground shadow-float nav:top-22"
        >
          <span>A new version of gatheredfrom is ready.</span>
          <Button size="sm" variant="secondary" onClick={() => void refreshToNewVersion()} disabled={refreshing}>
            {refreshing ? 'Saving…' : 'Refresh'}
          </Button>
        </div>
      )}
    </div>
  )
}
